import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Shield, Lock, Mail, KeyRound, ArrowRight, AlertTriangle } from 'lucide-react';

export default function AdminLogin() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSession, setOtpSession] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { adminLoginStart, adminVerifyOtp } = useAuth();
  const navigate = useNavigate();

  const onCreds = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await adminLoginStart(email, password);
      setOtpSession(data.otp_session_id);
      setDevOtp(data.dev_otp || '');
      setStep(2);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const onOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await adminVerifyOtp(otpSession, otp);
      navigate('/secure-portal-9x47/console');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout hideFooter>
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="neon-card rounded-lg p-8 relative border-orange-500/40">
          <div className="flex flex-col items-center mb-6">
            <div className="w-12 h-12 mb-3 grid place-items-center rounded border border-orange-500/60 bg-orange-500/10">
              <Shield className="w-6 h-6 text-orange-300" />
            </div>
            <h1 className="font-mono-display text-2xl text-white">Admin Access</h1>
            <p className="text-xs uppercase tracking-widest text-cyan-300 mt-1">Secure 2FA // Restricted</p>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5" />{error}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={onCreds} className="space-y-4">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-cyan-300">Admin Email</span>
                <div className="mt-1 flex items-center gap-2 bg-black/40 border border-zinc-800 focus-within:border-orange-400/70 rounded px-3 py-2">
                  <Mail className="w-4 h-4 text-zinc-500" />
                  <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@valorex.space" className="bg-transparent flex-1 outline-none text-white placeholder:text-zinc-600" />
                </div>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-cyan-300">Password</span>
                <div className="mt-1 flex items-center gap-2 bg-black/40 border border-zinc-800 focus-within:border-orange-400/70 rounded px-3 py-2">
                  <Lock className="w-4 h-4 text-zinc-500" />
                  <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-transparent flex-1 outline-none text-white" />
                </div>
              </label>
              <button disabled={loading} className="btn-neon w-full justify-center">
                {loading ? 'Authenticating...' : (<>Continue <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          ) : (
            <form onSubmit={onOtp} className="space-y-4">
              <div className="text-sm text-zinc-300 flex items-center gap-2 bg-black/40 border border-cyan-400/20 rounded p-3">
                <KeyRound className="w-4 h-4 text-cyan-300" /> A 6-digit OTP has been sent to <span className="text-cyan-300 ml-1">{email}</span>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-cyan-300">Email OTP</span>
                <input required value={otp} maxLength={6} onChange={(e) => setOtp(e.target.value)} placeholder="••••••" className="mt-1 w-full bg-black/40 border border-zinc-800 focus:border-orange-400/70 rounded px-3 py-2 outline-none text-white text-center font-mono-display text-2xl tracking-widest" />
              </label>
              <button disabled={loading} className="btn-neon w-full justify-center">
                {loading ? 'Verifying...' : (<>Verify &amp; Enter <ArrowRight className="w-4 h-4" /></>)}
              </button>
              {devOtp && (
                <p className="text-[11px] text-zinc-500 text-center">DEV OTP: <code className="text-cyan-300">{devOtp}</code> (shown only in dev mode)</p>
              )}
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-cyan-300">&larr; Back to site</Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
